/**
 * SelectInput Component
 *
 * Reusable select input with standardized styling matching TextInput/NumberInput.
 */

import clsx from 'clsx';
import { forwardRef } from 'react';
import type { SelectHTMLAttributes } from 'react';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectInputProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Input label (renders above select) */
  label?: string;
  /** Error message (renders below select with red styling) */
  error?: string | null;
  /** Helper text (renders below select with muted styling) */
  helperText?: string;
  /** Input size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Input style variant */
  variant?: 'default' | 'filled';
  /** Additional class name for the container */
  containerClassName?: string;
  /** Additional class name for the label */
  labelClassName?: string;
  /** Additional class name for the select */
  selectClassName?: string;
  /** Options to render */
  options: SelectOption[];
  /** Placeholder option text (renders as disabled first option) */
  placeholder?: string;
}

const sizeClasses = {
  sm: 'py-1 px-2 text-sm h-8',
  md: 'py-2 px-3 text-base h-9',
  lg: 'py-2.5 px-4 text-base h-10',
};

const variantClasses = {
  default: 'bg-bg',
  filled: 'bg-surface',
};

export const SelectInput = forwardRef<HTMLSelectElement, SelectInputProps>(function SelectInput({
  label,
  error,
  helperText,
  size = 'md',
  variant = 'default',
  containerClassName,
  labelClassName,
  selectClassName,
  className,
  id,
  options,
  placeholder,
  ...selectProps
}, ref) {
  const selectId = id || `select-${label?.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div className={clsx('flex flex-col', containerClassName)}>
      {label && (
        <label
          htmlFor={selectId}
          className={clsx(
            'block text-xs font-bold text-text-muted mb-2 uppercase tracking-[0.5px]',
            labelClassName
          )}
        >
          {label}
        </label>
      )}
      <select
        ref={ref}
        id={selectId}
        className={clsx(
          // Base styles
          'w-full border rounded-sm text-text box-border transition-all duration-fast cursor-pointer',
          // Focus styles
          'focus:outline-none focus:border-primary focus:shadow-[var(--focus-ring-blue-sm)] dark:focus:shadow-[var(--focus-ring-primary)]',
          // Size variant
          sizeClasses[size],
          // Background variant
          variantClasses[variant],
          // Error state
          error ? 'border-danger' : 'border-border',
          // Custom classes
          selectClassName,
          className
        )}
        {...selectProps}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <div className="mt-1 text-xs text-danger bg-red-50 dark:bg-red-900/20 py-1 px-2 rounded-sm border border-danger">
          {error}
        </div>
      )}
      {helperText && !error && (
        <div className="mt-1 text-xs text-text-muted">
          {helperText}
        </div>
      )}
    </div>
  );
});
